import React, { useState, useEffect } from 'react';
import axios from 'axios';
import Sidebar from './Sidebar';
import Header from './Header';
import { toast } from 'sonner';
import { useTheme } from '../contexts/ThemeContext';
import { useSidebar } from '../contexts/SidebarContext';
import { useAdmin } from '../contexts/AdminContext';
import { usePageTitle } from '../hooks/usePageTitle';
import { typography, spacing, borderRadius } from '../styles/typography';
import { Bell, Send, Loader2, AlertCircle, CheckCircle, Info, Lock, ArrowRight } from 'lucide-react';
import PageHeader from './ui/PageHeader';

const NotificationManagerPage = ({ onLogout }) => {
  usePageTitle('Notificações');
  const { theme } = useTheme();
  const { sidebarWidth, isMobile } = useSidebar();
  const { isSuperAdmin, isLoading } = useAdmin();

  const BACKEND_URL = import.meta.env.VITE_BACKEND_URL || '';
  const API = `${BACKEND_URL}/api`;

  const [notificationType, setNotificationType] = useState('info');
  const [title, setTitle] = useState('');
  const [message, setMessage] = useState('');
  const [link, setLink] = useState('');
  const [isSending, setIsSending] = useState(false);

  const notificationTypes = [
    { value: 'info', label: 'Informação', icon: Info, color: '#3B82F6' },
    { value: 'success', label: 'Sucesso', icon: CheckCircle, color: '#10B981' },
    { value: 'warning', label: 'Aviso', icon: AlertCircle, color: '#F59E0B' },
    { value: 'error', label: 'Erro', icon: AlertCircle, color: '#EF4444' }
  ];

  const handleSend = async (e) => {
    e.preventDefault();

    if (!title.trim() || !message.trim()) {
      toast.error('Preencha título e mensagem');
      return;
    }

    setIsSending(true);

    try {
      const token = localStorage.getItem('token');
      
      const payload = {
        type: notificationType,
        title: title.trim(),
        message: message.trim()
      };
      
      // Adicionar link apenas se houver valor
      if (link.trim()) {
        payload.link = link.trim();
      }

      const response = await axios.post(
        `${API}/notifications/broadcast`,
        payload,
        {
          headers: { Authorization: `Bearer ${token}` }
        }
      );

      toast.success(response.data.message || 'Notificação enviada com sucesso!');
      
      // Limpar formulário
      setTitle('');
      setMessage('');
      setLink('');
      setNotificationType('info');

    } catch (error) {
      console.error('Erro completo:', error);
      console.error('Response data:', error.response?.data);
      
      // Extrair mensagem de erro corretamente
      let errorMessage = 'Erro ao enviar notificação';
      
      if (error.response?.data?.detail) {
        const detail = error.response.data.detail;
        
        // Se detail é string
        if (typeof detail === 'string') {
          errorMessage = detail;
        }
        // Se detail é array (erros de validação Pydantic)
        else if (Array.isArray(detail)) {
          const errors = detail.map(err => {
            if (typeof err === 'string') return err;
            if (err.msg) return `${err.loc ? err.loc.join('.') + ': ' : ''}${err.msg}`;
            return JSON.stringify(err);
          });
          errorMessage = errors.join('; ');
        }
      } else if (error.response?.status === 422) {
        errorMessage = 'Erro de validação. Verifique os campos.';
      } else if (error.message) {
        errorMessage = error.message;
      }
      
      toast.error(errorMessage);
    } finally {
      setIsSending(false);
    }
  };

  // Verificação de acesso
  if (isLoading) {
    return (
      <div style={{ display: 'flex', minHeight: '100vh', background: 'var(--dp-bg-primary)' }}>
        <Sidebar onLogout={onLogout} />
        <Header />
        <main className="dp-main-content" style={{
          marginLeft: isMobile ? 0 : `${sidebarWidth}px`,
          marginTop: '60px',
          flex: 1,
          minWidth: 0,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center'
        }}>
          <Loader2 className="w-8 h-8 animate-spin" style={{ color: 'var(--dp-blue-500)' }} />
        </main>
      </div>
    );
  }

  if (!isSuperAdmin) {
    return (
      <div style={{ display: 'flex', minHeight: '100vh', background: 'var(--dp-bg-primary)' }}>
        <Sidebar onLogout={onLogout} />
        <Header />
        <main className="dp-main-content" style={{
          marginLeft: isMobile ? 0 : `${sidebarWidth}px`,
          marginTop: '60px',
          flex: 1,
          minWidth: 0,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center'
        }}>
          <div style={{ textAlign: 'center', maxWidth: '400px' }}>
            <Lock size={64} style={{ color: 'var(--dp-text-secondary)', margin: '0 auto 16px' }} />
            <h2 style={{ color: 'var(--dp-text-primary)', fontSize: '24px', marginBottom: '8px' }}>
              Acesso Restrito
            </h2>
            <p style={{ color: 'var(--dp-text-secondary)' }}>
              Esta página é exclusiva para administradores do sistema.
            </p>
          </div>
        </main>
      </div>
    );
  }

  return (
    <div style={{ display: 'flex', minHeight: '100vh', background: 'var(--dp-bg-primary)' }}>
      <Sidebar onLogout={onLogout} />
      <Header />
      
      <main className="dp-main-content" style={{
        marginLeft: isMobile ? 0 : `${sidebarWidth}px`,
        marginTop: '60px',
        flex: 1,
        minWidth: 0,
        transition: 'margin-left 0.3s ease',
        animation: 'dp-pageFadeIn 0.35s ease',
      }}>
        {/* Header — padrão premium compartilhado */}
        <PageHeader
          icon={<Bell size={20} color={'var(--dp-blue-500)'} />}
          title="Gerenciar Notificações"
          description="Envie notificações globais para todos os usuários ativos do sistema"
        />

        {/* Form */}
        <div className="dp-top-accent" style={{
          background: 'var(--dp-bg-card)',
          border: '1px solid var(--dp-border-subtle)',
          borderRadius: borderRadius.card,
          padding: spacing.xl
        }}>
          <form onSubmit={handleSend}>
            {/* Tipo de Notificação */}
            <div style={{ marginBottom: spacing.lg }}>
              <label style={{
                display: 'block',
                fontSize: '14px',
                fontWeight: '600',
                color: 'var(--dp-text-primary)',
                marginBottom: spacing.sm
              }}>
                Tipo de Notificação
              </label>
              <div className="cq-grid-4" style={{ display: 'grid', gridTemplateColumns: isMobile ? 'repeat(2, 1fr)' : 'repeat(4, 1fr)', gap: spacing.sm }}>
                {notificationTypes.map((type) => (
                  <button
                    key={type.value}
                    type="button"
                    onClick={() => setNotificationType(type.value)}
                    style={{
                      padding: spacing.md,
                      borderRadius: borderRadius.button,
                      border: `2px solid ${notificationType === type.value ? type.color : 'var(--dp-border-subtle)'}`,
                      background: notificationType === type.value ? `${type.color}15` : 'var(--dp-bg-card)',
                      cursor: 'pointer',
                      transition: 'all 0.2s',
                      display: 'flex',
                      flexDirection: 'column',
                      alignItems: 'center',
                      gap: spacing.xs
                    }}
                  >
                    <type.icon size={24} color={type.color} />
                    <span style={{
                      fontSize: '13px',
                      fontWeight: '600',
                      color: notificationType === type.value ? type.color : 'var(--dp-text-primary)'
                    }}>
                      {type.label}
                    </span>
                  </button>
                ))}
              </div>
            </div>

            {/* Título */}
            <div style={{ marginBottom: spacing.lg }}>
              <label style={{
                display: 'block',
                fontSize: '14px',
                fontWeight: '600',
                color: 'var(--dp-text-primary)',
                marginBottom: spacing.sm
              }}>
                Título *
              </label>
              <input
                type="text"
                value={title}
                onChange={(e) => setTitle(e.target.value)}
                placeholder="Ex: Nova atualização disponível!"
                required
                maxLength={100}
                style={{
                  width: '100%',
                  padding: '14px 16px',
                  borderRadius: borderRadius.input,
                  border: '1px solid var(--dp-border-subtle)',
                  background: 'var(--dp-bg-card)',
                  color: 'var(--dp-text-primary)',
                  fontSize: '15px',
                  outline: 'none',
                  transition: 'all 0.2s'
                }}
                onFocus={(e) => {
                  e.target.style.borderColor = 'var(--dp-blue-500)';
                  e.target.style.boxShadow = '0 0 0 3px color-mix(in srgb, var(--dp-blue-500) 12%, transparent)';
                }}
                onBlur={(e) => {
                  e.target.style.borderColor = 'var(--dp-border-subtle)';
                  e.target.style.boxShadow = 'none';
                }}
              />
              <div style={{
                fontSize: '12px',
                color: 'var(--dp-text-secondary)',
                marginTop: spacing.xs,
                textAlign: 'right'
              }}>
                {title.length}/100
              </div>
            </div>

            {/* Mensagem */}
            <div style={{ marginBottom: spacing.lg }}>
              <label style={{
                display: 'block',
                fontSize: '14px',
                fontWeight: '600',
                color: 'var(--dp-text-primary)',
                marginBottom: spacing.sm
              }}>
                Mensagem *
              </label>
              <textarea
                value={message}
                onChange={(e) => setMessage(e.target.value)}
                placeholder="Descreva a atualização ou informação que deseja compartilhar..."
                required
                maxLength={500}
                rows={5}
                style={{
                  width: '100%',
                  padding: '14px 16px',
                  borderRadius: borderRadius.input,
                  border: '1px solid var(--dp-border-subtle)',
                  background: 'var(--dp-bg-card)',
                  color: 'var(--dp-text-primary)',
                  fontSize: '15px',
                  outline: 'none',
                  transition: 'all 0.2s',
                  resize: 'vertical',
                  fontFamily: 'inherit'
                }}
                onFocus={(e) => {
                  e.target.style.borderColor = 'var(--dp-blue-500)';
                  e.target.style.boxShadow = '0 0 0 3px color-mix(in srgb, var(--dp-blue-500) 12%, transparent)';
                }}
                onBlur={(e) => {
                  e.target.style.borderColor = 'var(--dp-border-subtle)';
                  e.target.style.boxShadow = 'none';
                }}
              />
              <div style={{
                fontSize: '12px',
                color: 'var(--dp-text-secondary)',
                marginTop: spacing.xs,
                textAlign: 'right'
              }}>
                {message.length}/500
              </div>
            </div>

            {/* Link (opcional) */}
            <div style={{ marginBottom: spacing.xl }}>
              <label style={{
                display: 'block',
                fontSize: '14px',
                fontWeight: '600',
                color: 'var(--dp-text-primary)',
                marginBottom: spacing.sm
              }}>
                Link (opcional)
              </label>
              <input
                type="text"
                value={link}
                onChange={(e) => setLink(e.target.value)}
                placeholder="Ex: /tutoriais ou https://..."
                style={{
                  width: '100%',
                  padding: '14px 16px',
                  borderRadius: borderRadius.input,
                  border: '1px solid var(--dp-border-subtle)',
                  background: 'var(--dp-bg-card)',
                  color: 'var(--dp-text-primary)',
                  fontSize: '15px',
                  outline: 'none',
                  transition: 'all 0.2s'
                }}
                onFocus={(e) => {
                  e.target.style.borderColor = 'var(--dp-blue-500)';
                  e.target.style.boxShadow = '0 0 0 3px color-mix(in srgb, var(--dp-blue-500) 12%, transparent)';
                }}
                onBlur={(e) => {
                  e.target.style.borderColor = 'var(--dp-border-subtle)';
                  e.target.style.boxShadow = 'none';
                }}
              />
              <div style={{
                fontSize: '12px',
                color: 'var(--dp-text-secondary)',
                marginTop: spacing.xs
              }}>
                Link interno (ex: /tutoriais) ou externo (ex: https://site.com)
              </div>
            </div>

            {/* Botão Enviar */}
            <button
              type="submit"
              className="dp-btn-cta"
              disabled={isSending}
              style={{ width: '100%' }}
            >
              {isSending ? (
                <>
                  <Loader2 size={16} className="animate-spin" />
                  Enviando...
                </>
              ) : (
                <>
                  <Send size={16} />
                  Enviar Notificação para Todos
                  <ArrowRight size={16} />
                </>
              )}
            </button>
          </form>
        </div>

        {/* Info Box */}
        <div style={{
          background: theme.name === 'dark' ? 'rgba(59, 130, 246, 0.1)' : 'rgba(59, 130, 246, 0.05)',
          border: `1px solid #3B82F6`,
          borderRadius: borderRadius.card,
          padding: spacing.lg,
          marginTop: spacing.lg,
          display: 'flex',
          gap: spacing.md
        }}>
          <Info size={24} color="#3B82F6" style={{ flexShrink: 0 }} />
          <div>
            <h3 style={{ fontSize: '16px', fontWeight: '700', color: 'var(--dp-text-primary)', margin: `0 0 ${spacing.xs} 0` }}>
              Informações Importantes
            </h3>
            <ul style={{ fontSize: '14px', color: 'var(--dp-text-secondary)', margin: 0, paddingLeft: '20px', lineHeight: '1.8' }}>
              <li>A notificação será enviada para <strong style={{ color: 'var(--dp-text-primary)' }}>todos os usuários ativos</strong> do sistema</li>
              <li>Usuários receberão a notificação no sino (🔔) do cabeçalho</li>
              <li>Use <strong style={{ color: 'var(--dp-text-primary)' }}>Tipo Info</strong> para atualizações gerais</li>
              <li>Use <strong style={{ color: 'var(--dp-text-primary)' }}>Tipo Sucesso</strong> para novas funcionalidades</li>
              <li>Use <strong style={{ color: 'var(--dp-text-primary)' }}>Tipo Aviso</strong> para manutenções programadas</li>
              <li>O campo Link é opcional, mas recomendado para direcionar usuários</li>
            </ul>
          </div>
        </div>
      </main>
    </div>
  );
};

export default NotificationManagerPage;
